import { format } from "date-fns"
import { useEffect, useState } from "react"
import { LOCAL_TIME_ZONE } from "@/shared/lib/format-date"
import { Button } from "@/shared/ui/button"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/shared/ui/dialog"
import { Label } from "@/shared/ui/label"
import { DateTimeField } from "@/shared/ui/date-time-field"

const INPUT_FORMAT = "yyyy-MM-dd'T'HH:mm"

/** Ближайшие удобные слоты: чтобы не набирать дату руками в типичном случае. */
function quickSlots(now: Date): Array<{ label: string; date: Date }> {
	const at = (days: number, hours: number) => {
		const date = new Date(now)
		date.setDate(date.getDate() + days)
		date.setHours(hours, 0, 0, 0)
		return date
	}
	return [
		{ label: "Сегодня в 19:00", date: at(0, 19) },
		{ label: "Завтра в 10:00", date: at(1, 10) },
		{ label: "Завтра в 19:00", date: at(1, 19) },
	].filter((slot) => slot.date.getTime() > now.getTime() + 60_000)
}

export function ScheduleDialog({
	open,
	onOpenChange,
	initial,
	onSubmit,
	pending,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	/** Текущее время публикации, если переносим. */
	initial: string | null
	onSubmit: (date: Date) => void
	pending: boolean
}) {
	const [value, setValue] = useState("")
	const now = new Date()

	useEffect(() => {
		if (!open) return
		const start = initial ? new Date(initial) : quickSlots(new Date())[0]?.date
		setValue(start ? format(start, INPUT_FORMAT) : "")
	}, [open, initial])

	const date = value ? new Date(value) : null
	const inFuture = !!date && !Number.isNaN(date.getTime()) && date.getTime() > Date.now()

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{initial ? "Перенести публикацию" : "Запланировать публикацию"}</DialogTitle>
					<DialogDescription>
						Пост уйдёт в канал сам в указанное время. Время по поясу {LOCAL_TIME_ZONE}.
					</DialogDescription>
				</DialogHeader>
				<div className="flex flex-wrap gap-2">
					{quickSlots(now).map((slot) => (
						<button
							key={slot.label}
							type="button"
							onClick={() => setValue(format(slot.date, INPUT_FORMAT))}
							className="rounded-full bg-secondary px-3 py-1.5 text-xs font-medium text-secondary-foreground transition-colors hover:bg-accent"
						>
							{slot.label}
						</button>
					))}
				</div>
				<div className="space-y-2">
					<Label htmlFor="schedule-at">Дата и время</Label>
					<DateTimeField id="schedule-at" value={value} min={now} onChange={setValue} />
					{value && !inFuture ? (
						<p className="text-sm text-destructive">Выберите время в будущем</p>
					) : null}
				</div>
				<DialogFooter className="gap-2 sm:gap-2">
					<Button variant="secondary" onClick={() => onOpenChange(false)}>
						Отмена
					</Button>
					<Button
						disabled={!inFuture || pending}
						isLoading={pending}
						onClick={() => date && onSubmit(date)}
					>
						{initial ? "Перенести" : "Запланировать"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
